import { useEffect, useRef, useState } from "react";
import { FileUploader } from "react-drag-drop-files";
import { uploadVideo, useVideoStatus } from "../../../js/api/member-profile/member-profile";
import { SharedIcon } from "../shared/shared-icon";
import { ImageField } from "./image-field";
import { CharacterCount } from "./character-count";
import { formatSize } from "../../../js/upload-limits";

/**
 * A video: either an uploaded file or a link, never both.
 *
 * EXACTLY ONE SOURCE is the server's rule — no source is "Missing", two is "NotAllowed", and both
 * come back keyed to embedUrl because the member is choosing one thing in one place.
 *
 * The design shows both routes at once — a drop area, then "Or paste YouTube or Vimeo link" — and
 * not the radio pair this used to render. "Exactly one" is still true by construction rather than
 * by validation: attaching a file clears and disables the URL, and removing it gives the URL back.
 * The server still refuses a contradiction if anything else manages to post one.
 *
 * The allowed hosts and every limit are stated up front rather than left to a failed save, and
 * they come from configuration — the same values the server matches against.
 */
export function VideoFields({ form, update, errors, settings, copy }) {
  const [uploadError, setUploadError] = useState(null);

  // The upload in flight, or null: { name, loaded, total, startedAt }. What the progress bar and
  // its "12MB of 34MB – 12 seconds remaining" line are drawn from.
  const [upload, setUpload] = useState(null);
  const abortRef = useRef(null);

  const isUploading = upload !== null;
  const hasFile = form.videoFile.length > 0;
  const video = hasFile ? form.videoFile[0] : null;

  // HEVC uploads are converted to H.264 on the server after upload, then swapped in on the same
  // media item. Nothing here changes when that finishes - the key is the same and the preview is
  // the member's own file - so this only drives the note under the file.
  const { data: conversionStatus } = useVideoStatus(video?.key);

  // An upload still going when the member leaves the page is abandoned, not left running.
  useEffect(() => () => abortRef.current?.abort(), []);

  const handleFile = async (file) => {
    setUploadError(null);

    if (file.size > settings.videoMaxFileSizeBytes) {
      setUploadError(
        template(copy.videoTooLarge, formatSize(file.size), formatSize(settings.videoMaxFileSizeBytes))
      );
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;

    const startedAt = Date.now();
    setUpload({ name: file.name, loaded: 0, total: file.size, startedAt });

    const result = await uploadVideo(file, {
      signal: controller.signal,
      onProgress: ({ loaded, total }) =>
        setUpload((current) => current && { ...current, loaded, total: total || file.size }),
    });

    abortRef.current = null;
    setUpload(null);

    if (result.isCancelled) {
      return;
    }

    if (!result.isSuccess) {
      // The server's own wording: it knows whether the file was too long, the wrong container, or
      // simply unreadable, and the duration limit is only enforceable once the header is read.
      setUploadError(result.message);
      return;
    }

    // The server answers with a key, not a URL — the item has not been saved yet. The file is
    // right here in the browser though, so the preview plays THAT. `previewUrl` never goes back
    // over the wire; toRequest sends the bare key.
    //
    // Attaching a file clears the link, which is what keeps "exactly one" true by construction.
    update({
      videoFile: [
        { key: result.mediaKey, url: null, previewUrl: URL.createObjectURL(file), name: file.name, altText: "" },
      ],
      embedUrl: "",
    });
  };

  const removeVideo = () => {
    // Released as the preview goes, not on unmount: a blob nobody can see is a leak for the life
    // of the page, and a video is a big one.
    if (video?.previewUrl) {
      URL.revokeObjectURL(video.previewUrl);
    }

    update({ videoFile: [] });
  };

  // A saved video has no alt text — media carries the uploaded file's name instead, which is what
  // the member recognises. The fallback covers a media item that has lost both.
  const videoName = video ? video.name || video.altText || copy.videoFallbackName : "";

  return (
    <>
      <fieldset className="member-profile-editor__field" data-testid="video-source">
        <legend className="member-profile-editor__field-label">{copy.videoUploadLabel}</legend>

        {hasFile ? (
          <div className="member-profile-editor__video-added" data-testid="video-file">
            {/* The first frame of the video itself, as the design's small thumbnail. Muted and
                not autoplaying: it is a picture of the file, not a player. */}
            {video.url || video.previewUrl ? (
              <video
                className="member-profile-editor__video-thumb"
                src={`${video.url || video.previewUrl}#t=0.1`}
                preload="metadata"
                muted
                playsInline
                aria-hidden="true"
                tabIndex={-1}
              />
            ) : null}

            <p className="member-profile-editor__video-name" data-testid="video-file-name">
              {videoName}
            </p>

            <p className="member-profile-editor__video-status">
              {copy.videoAdded}
              <SharedIcon
                className="member-profile-editor__video-status-icon"
                iconName="#sprite-icon-tick"
                aria-hidden="true"
              />
            </p>

            <button
              type="button"
              className="member-profile-editor__video-remove"
              data-testid="video-file-remove"
              aria-label={template(copy.removeVideo, videoName)}
              onClick={removeVideo}
            >
              <SharedIcon iconName="#sprite-bin" aria-hidden="true" />
            </button>
          </div>
        ) : isUploading ? (
          <UploadProgress
            upload={upload}
            copy={copy}
            onCancel={() => abortRef.current?.abort()}
          />
        ) : (
          <FileUploader
            handleChange={handleFile}
            name="video"
            // The picker offers exactly what the server accepts, from configuration. Extensions
            // arrive as ".mp4"; this control wants them bare and upper-cased.
            types={settings.videoAllowedExtensions.map((extension) =>
              extension.replace(".", "").toUpperCase()
            )}
            disabled={isUploading}
            classes="member-profile-editor__dropzone member-profile-editor__dropzone--video"
          >
            <span className="member-profile-editor__dropzone-inner" data-testid="video-dropzone">
              <SharedIcon
                className="member-profile-editor__dropzone-icon"
                iconName="#sprite-video-upload"
                aria-hidden="true"
              />

              <span className="member-profile-editor__dropzone-label">{copy.videoDropLabel}</span>

              <span className="member-profile-editor__dropzone-max">
                {template(copy.videoMaxSize, formatSize(settings.videoMaxFileSizeBytes))}
              </span>

              {settings.videoMaxDurationSeconds > 0 ? (
                <span className="member-profile-editor__dropzone-note">
                  {template(
                    copy.videoMaxDuration,
                    Math.round(settings.videoMaxDurationSeconds / 60)
                  )}
                </span>
              ) : null}

              {/* A span, not a button: the whole dropzone is already a label wrapping the file
                  input, and a real button nested in it would swallow the click that opens the
                  picker. */}
              <span className="button member-profile-editor__browse">{copy.videoBrowse}</span>
            </span>
          </FileUploader>
        )}

        {hasFile && conversionStatus === "converting" ? (
          <p className="member-profile-editor__hint" role="status" data-testid="video-converting">
            {copy.videoConverting}
          </p>
        ) : null}

        {hasFile && conversionStatus === "failed" ? (
          <p className="member-profile-editor__error" role="alert" data-testid="video-convert-failed">
            {copy.videoConvertFailed}
          </p>
        ) : null}

        {uploadError ? (
          <p className="member-profile-editor__error" role="alert" data-testid="video-upload-error">
            {uploadError}
          </p>
        ) : null}
      </fieldset>

      <div className="member-profile-editor__field">
        <label className="member-profile-editor__field-label" htmlFor="video-embed-url">
          {copy.videoLinkLabel}
        </label>

        <input
          id="video-embed-url"
          type="url"
          className="member-profile-editor__input"
          data-testid="video-embed-url"
          placeholder={copy.videoLinkPlaceholder}
          value={form.embedUrl}
          disabled={hasFile}
          onChange={(event) => update({ embedUrl: event.target.value })}
        />

        <p className="member-profile-editor__hint">
          {hasFile
            ? copy.videoLinkDisabled
            : template(copy.videoLinkHint, settings.videoEmbedAllowedHosts.join(", "))}
        </p>

        {errors.embedUrl ? (
          <p className="member-profile-editor__error" role="alert" data-testid="error-embed-url">
            {errors.embedUrl}
          </p>
        ) : null}
      </div>

      <ImageField
        label={copy.videoThumbnailLabel}
        hint={copy.videoThumbnailHint}
        images={form.posterImage}
        onChange={(posterImage) => update({ posterImage })}
        maxFileSizeBytes={settings.imageMaxFileSizeBytes}
        testId="video-poster-image"
        error={errors.posterImage}
        copy={copy}
        source="video"
        previews={[
          { label: "Video", aspect: 16 / 9 },
          { label: "Card", aspect: 1 },
        ]}
      />

      <div className="member-profile-editor__field">
        <label className="member-profile-editor__field-label" htmlFor="video-description">
          {copy.videoAboutLabel}
        </label>

        <p className="member-profile-editor__hint">{copy.videoAboutHint}</p>

        <textarea
          id="video-description"
          className="member-profile-editor__textarea"
          data-testid="video-description"
          rows={6}
          value={form.description}
          onChange={(event) => update({ description: event.target.value })}
        />

        <CharacterCount
          value={form.description}
          max={settings.descriptionMaxLength}
          testId="video-description-counter"
        />

        {errors.description ? (
          <p className="member-profile-editor__error" role="alert" data-testid="error-description">
            {errors.description}
          </p>
        ) : null}
      </div>
    </>
  );
}

/**
 * The upload in flight, as the design's uploading frame draws it: the camera icon, the file name,
 * a progress bar with a ✕ to stop it, and "12MB of 34MB – 12 seconds remaining".
 *
 * The time left is estimated from the rate so far, and left off until there is a rate to go on —
 * a guess from the first few kilobytes would only jump about.
 */
function UploadProgress({ upload, copy, onCancel }) {
  const percent = upload.total > 0 ? Math.min(100, Math.round((upload.loaded / upload.total) * 100)) : 0;

  const elapsedSeconds = (Date.now() - upload.startedAt) / 1000;
  const rate = elapsedSeconds > 0 ? upload.loaded / elapsedSeconds : 0;
  const secondsLeft = rate > 0 ? Math.max(0, Math.ceil((upload.total - upload.loaded) / rate)) : null;

  const sizes = template(copy.videoUploadProgress, formatSize(upload.loaded), formatSize(upload.total));
  const remaining = secondsLeft !== null && upload.loaded > 0 ? template(copy.videoSecondsRemaining, secondsLeft) : null;

  return (
    <div className="member-profile-editor__video-uploading" data-testid="video-uploading">
      <SharedIcon
        className="member-profile-editor__dropzone-icon"
        iconName="#sprite-video-upload"
        aria-hidden="true"
      />

      <p className="member-profile-editor__video-name">{upload.name}</p>

      <div className="member-profile-editor__progress-row">
        <progress
          className="member-profile-editor__progress"
          max={100}
          value={percent}
          aria-label={copy.uploading}
          data-testid="video-upload-progress"
        >
          {percent}%
        </progress>

        <button
          type="button"
          className="member-profile-editor__progress-cancel"
          data-testid="video-upload-cancel"
          aria-label={copy.videoCancelUpload}
          onClick={onCancel}
        >
          <SharedIcon iconName="#sprite-icon-cross" aria-hidden="true" />
        </button>
      </div>

      <p className="member-profile-editor__progress-text">
        {remaining ? `${sizes} – ${remaining}` : sizes}
      </p>
    </div>
  );
}

/** Fills "{0}" and "{1}" placeholders, so the copy decides where the values sit. */
const template = (text, first, second) =>
  (text ?? "").replace("{0}", first).replace("{1}", second);
